import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Reveal from '@/components/feature/Reveal';

const ENDPOINT = 'https://debuqhzswiqcosjkuktw.supabase.co/functions/v1/send-contact-email';
const TO_EMAIL = 'contact@luvira.co.jp';
const PROJECT_ID = '1cfcb9a7-5410-444f-8143-602b7e61a697';

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-[#12121f] border border-[#2a2a3e] text-white placeholder:text-[#7a7a8a] focus:outline-none focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed]/40 transition-colors text-sm';

export default function Contact() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const service = searchParams.get('service') || '';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      navigate('/contact/thanks/');
      return;
    }

    const company = ((formData.get('company') as string) || '').trim();
    const name = ((formData.get('name') as string) || '').trim();
    const email = ((formData.get('email') as string) || '').trim();
    const tel = ((formData.get('tel') as string) || '').trim();
    const type = ((formData.get('type') as string) || '').trim();
    const serviceValue = ((formData.get('service') as string) || '').trim();
    const message = ((formData.get('message') as string) || '').trim();

    if (message.length > 500) {
      setError('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    const payload = {
      project_id: PROJECT_ID,
      email,
      body: {
        会社名: company,
        お名前: name,
        メールアドレス: email,
        電話番号: tel,
        お問い合わせ種別: type,
        対象サービス: serviceValue,
        お問い合わせ内容: message,
      },
    };

    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore */
      }

      const isSuccess =
        res.ok &&
        (!parsed || parsed?.success !== false) &&
        (parsed as Record<string, unknown> | null)?.error === undefined;

      if (isSuccess) {
        navigate('/contact/thanks/');
      } else {
        const data = parsed as Record<string, unknown> | null;
        const meta = data?.meta as Record<string, unknown> | undefined;
        const msg =
          (data?.error as string) ||
          (data?.message as string) ||
          (meta?.message as string) ||
          (meta?.detail as string) ||
          '送信に失敗しました。時間をおいて再度お試しください。';
        setError(msg);
      }
    } catch {
      setError('ネットワークエラーが発生しました。再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-32 md:pt-40 pb-20 md:pb-28 px-4 md:px-6 relative overflow-hidden bg-[#0d0d1a]">
      <div
        className="blob w-[400px] h-[400px] top-0 right-0"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
      ></div>

      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1e1e3f] border border-[#3b3b6b]">
            <span className="w-2 h-2 rounded-full bg-[#a855f7]"></span>
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[#a78bfa]">Contact</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-extrabold text-white">お問い合わせ</h1>
          <p className="mt-4 text-[#a0a0b0] text-base md:text-lg leading-relaxed">
            サービスに関するご質問や見積もり依頼など、お気軽にお問い合わせください。
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-3xl p-6 md:p-8 space-y-6"
              data-readdy-form="contact"
            >
              {/* Honeypot */}
              <div className="website-field" aria-hidden="true">
                <input
                  id="website_alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <input type="hidden" name="service" value={service} />

              {service && (
                <div className="rounded-xl bg-[#7c3aed]/10 border border-[#7c3aed]/30 px-4 py-3 text-sm text-[#a78bfa]">
                  お問い合わせ対象：{service}
                </div>
              )}

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white mb-2">
                  会社名 <span className="text-[#a855f7]">*</span>
                </label>
                <input id="company" name="company" type="text" required className={inputClass} placeholder="株式会社〇〇" />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  お名前 <span className="text-[#a855f7]">*</span>
                </label>
                <input id="name" name="name" type="text" required className={inputClass} placeholder="山田 太郎" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    メールアドレス <span className="text-[#a855f7]">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className={inputClass}
                    placeholder="example@company.co.jp"
                  />
                </div>
                <div>
                  <label htmlFor="tel" className="block text-sm font-medium text-white mb-2">
                    電話番号
                  </label>
                  <input id="tel" name="tel" type="tel" className={inputClass} placeholder="03-0000-0000" />
                </div>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-white mb-2">
                  お問い合わせ種別 <span className="text-[#a855f7]">*</span>
                </label>
                <select id="type" name="type" required className={inputClass} defaultValue="サービスについて">
                  <option value="サービスについて">サービスについて</option>
                  <option value="見積もり依頼">見積もり依頼</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  お問い合わせ内容 <span className="text-[#a855f7]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className={`${inputClass} resize-none`}
                  placeholder="お問い合わせ内容をご記入ください"
                ></textarea>
                <p className="mt-2 text-xs text-[#7a7a8a] text-right">500文字以内</p>
              </div>

              {error && (
                <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 w-full text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 hover:scale-[1.02] hover:shadow-[0_14px_30px_rgba(124,58,237,0.4)] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    送信する
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2">
            <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-3xl p-6 md:p-8 space-y-6 h-full">
              <h2 className="text-lg font-extrabold text-white">お問い合わせの前に</h2>
              <div className="space-y-5">
                <div className="flex gap-4 group">
                  <div className="icon-outline shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">メール</p>
                    <a href={`mailto:${TO_EMAIL}`} className="text-sm text-[#a0a0b0] hover:text-[#a78bfa] transition-colors cursor-pointer">
                      {TO_EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="icon-outline shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">お電話</p>
                    <a href="tel:050-5367-5817" className="text-sm text-[#a0a0b0] hover:text-[#a78bfa] transition-colors cursor-pointer">
                      050-5367-5817
                    </a>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="icon-outline shrink-0">
                    <i className="ri-video-chat-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">全国オンライン対応</p>
                    <p className="text-sm text-[#a0a0b0]">ビデオ通話でのお打ち合わせが可能です。</p>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="icon-outline shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">対応時間</p>
                    <p className="text-sm text-[#a0a0b0]">平日 10:00〜18:00</p>
                  </div>
                </div>
              </div>
              <p className="pt-4 border-t border-[#2a2a3e] text-xs text-[#7a7a8a] leading-relaxed">
                ご入力いただいた内容は、2営業日以内に担当者よりご連絡いたします。
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}